import React, { useEffect, useMemo, useState } from "react";
import { compareSchoolYearDesc } from "../../../util/schoolYear";
import { useLocation } from "react-router-dom";
import LeftMenuSidebar from "../../../unicomponents/LeftMenuSidebar";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { useAuthContext } from "../../../context/AuthContext";
import { useUIContext } from "../../../context/UIContext";
import DropdownSchoolYear from "../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownHierarchyOne from "../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import DropdownHierarchyTwo from "../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyThree from "../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import DropdownTermDate from "../../../unicomponents/Dropdowns/DropdownTermDate";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import useTermPeriod from "../../../hooks/useTermPeriod";
import useParentEducation from "../../../hooks/useParentEducation";
import ParentEducationListPage from "./components/list/ParentEducationListPage";
import ParentEducationSettingsPage from "./components/settings/ParentEducationSettingsPage";
import styles from "./css/ParentEducation.module.css";
import HelpHyperLink from "../../../unicomponents/HelpHyperLink";

export default function ParentEducation() {
  const location = useLocation();
  const schoolId = location.pathname.split("/")[1];
  const { hasPermission } = useAuthContext();
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();

  const [activeMenu, setActiveMenu] = useState({
    menu_name: "list",
    menu_id: "list",
  });
  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    termId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
  });

  const [periods, setPeriods] = useState([]);
  const [attendances, setAttendances] = useState([]);

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });
  const {
    registrationInfoQuery: { data: registrationInfoData },
  } = useRegistrationInfo({ schoolid: schoolId });
  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod({ schoolid: schoolId });

  const canAccess = hasPermission(null, "can_access");
  const canRead = hasPermission(null, "can_read");
  const canCreate = hasPermission(null, "can_create");
  const canUpdate = hasPermission(null, "can_update");
  const canDelete = hasPermission(null, "can_delete");

  const {
    bootstrapQuery,
    createPeriodMutation,
    updatePeriodMutation,
    deletePeriodMutation,
    loadPreviousPeriodsMutation,
    upsertAttendanceBulkMutation,
  } = useParentEducation({ schoolid: schoolId });

  useEffect(() => {
    const d = bootstrapQuery.data;
    if (!d) return;
    setPeriods(d.periods || []);
    setAttendances(d.attendances || []);
  }, [bootstrapQuery.data]);

  useEffect(() => {
    if (filters.schoolYear !== null || !settingData?.length) return;
    const sortedYears = settingData
      .filter((x) => x.hierarchy_level === 0 && x.parent_id === 0)
      .sort((a, b) => compareSchoolYearDesc(a.school_year, b.school_year));
    if (!sortedYears.length) return;
    const initial = sortedYears[0];
    setFilters((prev) => ({
      ...prev,
      schoolYear: initial.school_year,
      schoolYearId: String(initial.id),
    }));
  }, [settingData, filters.schoolYear]);

  useEffect(() => {
    if (!bootstrapQuery.isError) return;
    handleToastCenterTop("학부모교육 데이터 로딩 실패");
  }, [bootstrapQuery.isError, handleToastCenterTop]);

  const students = useMemo(() => {
    if (!registrationInfoData || !settingData) return [];
    return registrationInfoData
      .filter((reg) => {
        if (
          filters.schoolYear &&
          String(reg.school_year) !== String(filters.schoolYear)
        ) {
          return false;
        }
        if (
          filters.hierarchyOneId &&
          filters.hierarchyOneId !== "0" &&
          !reg.organization_path?.includes(`/${filters.hierarchyOneId}`)
        ) {
          return false;
        }
        if (
          filters.hierarchyTwoId &&
          filters.hierarchyTwoId !== "0" &&
          !reg.organization_path?.includes(`/${filters.hierarchyTwoId}`)
        ) {
          return false;
        }
        if (
          filters.hierarchyThreeId &&
          filters.hierarchyThreeId !== "0" &&
          !reg.organization_path?.includes(`/${filters.hierarchyThreeId}`)
        ) {
          return false;
        }
        return true;
      })
      .map((reg) => {
        const pathIds = reg.organization_path?.split("/").filter(Boolean) || [];
        return {
          username: reg.username,
          fullname: reg.user?.fullname || "",
          school_year_id: pathIds[0] || "",
          hierarchy_one: pathIds[1] || "",
          hierarchy_two: pathIds[2] || "",
          hierarchy_three: pathIds[3] || "",
        };
      });
  }, [registrationInfoData, settingData, filters]);

  const menus = useMemo(() => {
    const base = [{ id: "list", menu_name: "list", menu_label: "조회/입력" }];
    if (canRead || canCreate || canUpdate || canDelete) {
      base.push({
        id: "settings",
        menu_name: "settings",
        menu_label: "설정",
      });
    }
    return base;
  }, [canRead, canCreate, canUpdate, canDelete]);

  useEffect(() => {
    const allowed = menus.some((menu) => menu.id === activeMenu.menu_id);
    if (!allowed) {
      setActiveMenu({ menu_name: "list", menu_id: "list" });
    }
  }, [menus, activeMenu.menu_id]);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions?.[0]?.text || "";
    if (name === "0") {
      setFilters({
        schoolYear: selectedOptionText,
        schoolYearId: value,
        termId: "",
        hierarchyOneId: "",
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      });
      return;
    }
    if (name === "termDate") {
      setFilters((prev) => ({ ...prev, termId: value }));
      return;
    }
    if (name === "1") {
      setFilters((prev) => ({
        ...prev,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
      }));
      return;
    }
    if (name === "2") {
      setFilters((prev) => ({
        ...prev,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
      }));
      return;
    }
    if (name === "3") {
      setFilters((prev) => ({ ...prev, hierarchyThreeId: value }));
    }
  };

  if (!canAccess) {
    return <SimpleAlertMessageBlock message="접근 권한이 없습니다." />;
  }

  return (
    <div className={`basicWidth ${styles.pageContainer}`}>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-2">
            <LeftMenuSidebar
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              menuData={menus}
              itemName="menu_name"
              itemLabel="menu_label"
            />
          </div>
          <div className="col-12 col-md-10 mt-3">
            <div className={styles.contentArea}>
              {activeMenu.menu_id === "list" && (
                <div className={styles.filterRow}>
                  <div style={{ width: "95px" }}>
                    <DropdownSchoolYear
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYearId={filters.schoolYearId}
                      allYears={false}
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownTermDate
                      termPeriodData={termPeriodData}
                      handleDropdownChange={handleDropdownChange}
                      termId={filters.termId}
                      schoolYearId={filters.schoolYearId}
                      hierarchyOneId={filters.hierarchyOneId}
                      hierarchyTwoId={filters.hierarchyTwoId}
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyOne
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyOneId={filters.hierarchyOneId}
                      hierarchyOneTitle={"과정"}
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyTwo
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyOneId={filters.hierarchyOneId}
                      hierarchyTwoId={filters.hierarchyTwoId}
                      hierarchyTwoTitle={"학년"}
                    />
                  </div>
                  <div style={{ width: "95px" }}>
                    <DropdownHierarchyThree
                      settingData={settingData}
                      handleDropdownChange={handleDropdownChange}
                      schoolYear={filters.schoolYear}
                      hierarchyTwoId={filters.hierarchyTwoId}
                      hierarchyThreeId={filters.hierarchyThreeId}
                      hierarchyThreeTitle={"반"}
                    />
                  </div>
                  <HelpHyperLink link={"https://youtu.be/miYu1-2cQ0g"} />
                </div>
              )}
              {activeMenu.menu_id === "list" && (
                <ParentEducationListPage
                  schoolId={schoolId}
                  filters={filters}
                  periods={periods}
                  students={students}
                  attendances={attendances}
                  settingData={settingData}
                  termPeriodData={termPeriodData}
                  canAccess={canAccess}
                  handleToastCenterTop={handleToastCenterTop}
                  upsertAttendanceBulkMutation={upsertAttendanceBulkMutation}
                />
              )}

              {activeMenu.menu_id === "settings" && (
                <ParentEducationSettingsPage
                  schoolId={schoolId}
                  periods={periods}
                  setPeriods={setPeriods}
                  settingData={settingData}
                  termPeriodData={termPeriodData}
                  canRead={canRead}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  handleToastCenterTop={handleToastCenterTop}
                  setToastConfirmModalState={setToastConfirmModalState}
                  createPeriodMutation={createPeriodMutation}
                  updatePeriodMutation={updatePeriodMutation}
                  deletePeriodMutation={deletePeriodMutation}
                  loadPreviousPeriodsMutation={loadPreviousPeriodsMutation}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
